/**
 * @fileoverview Removing an archived plant for good.
 *
 * Archiving is reversible and says so; this is the other door, and it has to
 * read like one. So it says what goes and how many of them, in the plant's own
 * numbers rather than a general warning, and it asks for the word to be typed
 * — the same word, in the same way, as deleting an account, because somebody
 * who has met one of these already knows what this is.
 *
 * A confirmation that can be cleared by reflex is not a confirmation. Typing
 * is the only thing on this screen that cannot be done by accident.
 *
 * Where: ArchivedPlants (DeletePlantDialog, a window of its own) and
 * PlantFormDialog's remove panel (DeletePlantConfirm, inside it).
 * API: none; the caller deletes.
 * Settings: none.
 * Words (en/es): plants.archive.delete.* (title, body, photoOne, photos,
 * noteOne, notes, forever, typeIt, cancel, confirm; and for
 * several plants titleMany, bodyMany, foreverMany); sitewide.saving (Button's loading word while the delete waits).
 * The foot is an ActionRow (B111): Keep it on the left, Delete for good on the right, 44px each.
 * Watch out: the word to type is DELETE in every language, as the account
 * dialog has it.
 *
 * @module DeletePlantDialog
 */

import React from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { FailureText } from '@/shared/components/ServerFailure';
import { useGrowLimits } from '@/shared/lib/growSettings';

/** The plant being deleted, what it will take with it, and the dialog's state. */
interface DeletePlantDialogProps {
  /** Whether the dialog is open. */
  isOpen: boolean;
  /** Told when the dialog asks to close; ignored while the delete is on its way. */
  onOpenChange: (open: boolean) => void;
  /** The plant's name, in the title. */
  plantName: string;
  /** How many photos go with it, said in the warning. */
  photos: number;
  /** How many notes go with it, said in the warning. */
  notes: number;
  /** True while the delete is on its way. */
  busy: boolean;
  /** Why the last attempt failed, or null. */
  error: string | null;
  /** Deletes the plant; only callable once the word has been typed. */
  onConfirm: () => void;
}

/**
 * The confirmation itself, without a window around it.
 *
 * Edit plant shows it in the panel that offered the choice, rather than
 * opening a second window over the first; the archive shows it in its own.
 * One body for both, so the words and the typed word cannot drift apart.
 *
 * @param props - The plant's name and counts, the delete's state, and what
 *   the two buttons do.
 */
export const DeletePlantConfirm: React.FC<{
  /** The plant's name, in the title. */
  plantName: string;
  /** How many plants are being deleted together; more than one changes the title and body to the plural. */
  count?: number;
  /** How many photos go with it. */
  photos: number;
  /** How many notes go with it. */
  notes: number;
  /** True while the delete is on its way. */
  busy: boolean;
  /** Why the last attempt failed, or null. */
  error: string | null;
  /** Deletes the plant; the button works only once the word is typed. */
  onConfirm: () => void;
  /** Keep it: closes, or goes back to the panel that offered the choice. */
  onCancel: () => void;
  /** Where the heading goes, when the caller has a back button beside it. */
  renderTitle?: (title: string) => React.ReactNode;
}> = ({ plantName, count = 1, photos, notes, busy, error, onConfirm, onCancel, renderTitle }) => {
  const { reactTranslate: t } = useTranslation();
  /** What has been typed in the box. */
  const [typed, setTyped] = React.useState('');
  /** The word has been typed exactly (capitals, spaces round it ignored). */
  /** The word to type, the server's (limits.DELETE_CONFIRM_WORD): typed exactly, in capitals; none can be confirmed before the app has heard it. */
  const word = useGrowLimits().DELETE_CONFIRM_WORD;
  const confirmed = word !== undefined && typed.trim() === word;
  /** Several plants at once, said in the plural. */
  const many = count > 1;
  const title = many ? t('plants.archive.delete.titleMany', { count }) : t('plants.archive.delete.title', { name: plantName });

  return (
    <>
      {renderTitle ? renderTitle(title) : (
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
      )}

      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {t(many ? 'plants.archive.delete.bodyMany' : 'plants.archive.delete.body', {
            photos: photos === 1 ? t('plants.archive.delete.photoOne') : t('plants.archive.delete.photos', { count: photos }),
            notes: notes === 1 ? t('plants.archive.delete.noteOne') : t('plants.archive.delete.notes', { count: notes }),
          })}
        </p>
        <p className="flex items-start gap-2 text-sm text-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{t(many ? 'plants.archive.delete.foreverMany' : 'plants.archive.delete.forever')}</span>
        </p>

        <div className="space-y-1.5">
          <Label htmlFor="delete-plant-confirm">
            {t('plants.archive.delete.typeIt', { word: word ?? '' })}
          </Label>
          <Input
            id="delete-plant-confirm"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            disabled={busy}
          />
        </div>

        {error && (
          <p className="whitespace-pre-line text-sm text-destructive"><FailureText text={error} /></p>
        )}
      </div>

      <ActionRow onCancel={onCancel} cancelLabel={t('plants.archive.delete.cancel')} disabled={busy}>
        <Button variant="destructive" onClick={onConfirm} loading={busy} busy="deleting" disabled={!confirmed || busy}>
          <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
          {t('plants.archive.delete.confirm')}
        </Button>
      </ActionRow>
    </>
  );
};

/**
 * The confirmation in a window of its own, as the archive page opens it. It
 * cannot be closed while the delete is on its way.
 *
 * @param props - DeletePlantDialogProps.
 */
export const DeletePlantDialog: React.FC<DeletePlantDialogProps> = ({
  isOpen, onOpenChange, plantName, photos, notes, busy, error, onConfirm,
}) => {
  const { reactTranslate: t } = useTranslation();
  return (
  <Dialog open={isOpen} onOpenChange={(open) => { if (!busy) onOpenChange(open); }}>
    <DialogContent className="sm:max-w-md">
      {/* Mounted afresh each time it opens, so a word left behind cannot arm
          the button for a different plant. */}
      {/* While it closes the confirmation is gone, but the box still needs a name for screen readers (B51). */}
      {!isOpen && <DialogTitle className="sr-only">{t('plants.archive.delete.dialogLabel')}</DialogTitle>}
      {isOpen && (
        <DeletePlantConfirm
          plantName={plantName}
          photos={photos}
          notes={notes}
          busy={busy}
          error={error}
          onConfirm={onConfirm}
          onCancel={() => onOpenChange(false)}
        />
      )}
    </DialogContent>
  </Dialog>
  );
};
